import React, { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Download, Mic, Square, Clock, FileAudio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import NeonButton from "@/components/NeonButton";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

const AudioRecorder = ({ audioContext, masterGain, isPlaying }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [maxDuration, setMaxDuration] = useState(30);
  const [recordings, setRecordings] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamDestRef = useRef(null);

  const startRecording = useCallback(async () => {
    if (!audioContext || !masterGain) {
      toast.error("Please start playing audio first");
      return;
    }

    try {
      // Create a MediaStreamDestination to capture audio
      streamDestRef.current = audioContext.createMediaStreamDestination();
      masterGain.connect(streamDestRef.current);

      const mediaRecorder = new MediaRecorder(streamDestRef.current.stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        
        const newRecording = {
          id: Date.now(),
          url,
          blob,
          duration: recordingTime,
          timestamp: new Date().toISOString(),
          name: `Recording ${recordings.length + 1}`
        };
        
        setRecordings(prev => [newRecording, ...prev]);
        setIsProcessing(false);
        toast.success("Recording saved!");
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxDuration * 60) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

      toast.success("Recording started");
    } catch (error) {
      console.error("Recording error:", error);
      toast.error("Failed to start recording");
    }
  }, [audioContext, masterGain, maxDuration, recordings.length, recordingTime]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (streamDestRef.current && masterGain) {
        try {
          masterGain.disconnect(streamDestRef.current);
        } catch (e) {}
      }
    }
  }, [isRecording, masterGain]);

  const downloadRecording = (recording) => {
    const a = document.createElement('a');
    a.href = recording.url;
    a.download = `${recording.name.replace(/\s+/g, '_')}.webm`;
    a.click();
    toast.success("Download started");
  };

  const deleteRecording = (id) => {
    setRecordings(prev => {
      const rec = prev.find(r => r.id === id);
      if (rec) URL.revokeObjectURL(rec.url);
      return prev.filter(r => r.id !== id);
    });
    toast.success("Recording deleted");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <Mic size={16} className="text-red-400" />
            Audio Recorder
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Record and export your sound sessions
          </p>
        </div>
      </div>

      {/* Recording Controls */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {isRecording ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={stopRecording}
                data-testid="stop-recording"
                className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.4)]"
              >
                <Square size={20} className="text-white" fill="white" />
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startRecording}
                disabled={!isPlaying}
                data-testid="start-recording"
                className={cn(
                  "w-14 h-14 rounded-full flex items-center justify-center transition-all",
                  isPlaying
                    ? "bg-gradient-to-r from-red-500 to-pink-500 shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                    : "bg-gray-700 cursor-not-allowed opacity-50"
                )}
              >
                <Mic size={20} className="text-white" />
              </motion.button>
            )}

            <div>
              <div className="flex items-center gap-2">
                {isRecording && (
                  <motion.div
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-2 h-2 rounded-full bg-red-500"
                  />
                )}
                <span className="font-mono text-xl text-white">
                  {formatTime(recordingTime)}
                </span>
              </div>
              <span className="text-xs text-gray-500">
                {isRecording ? "Recording..." : "Ready to record"}
              </span>
            </div>
          </div>

          <div className="text-right">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={14} className="text-gray-500" />
              <span className="text-xs text-gray-500">Max Duration</span>
            </div>
            <div className="flex items-center gap-2">
              <Slider
                value={[maxDuration]}
                onValueChange={([v]) => setMaxDuration(v)}
                min={1}
                max={59}
                step={1}
                disabled={isRecording}
                data-testid="max-duration-slider"
                className="w-24"
              />
              <span className="text-sm text-violet-400 font-mono w-12">
                {maxDuration} min
              </span>
            </div>
          </div>
        </div>

        {!isPlaying && !isRecording && (
          <div className="text-center py-2 text-xs text-yellow-500 bg-yellow-500/10 rounded-lg">
            Start playing audio to enable recording
          </div>
        )}
      </div>

      {/* Recordings List */}
      {recordings.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-300">Your Recordings</h5>
          
          {recordings.map((recording) => (
            <motion.div
              key={recording.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-3 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <FileAudio size={18} className="text-violet-400" />
                </div>
                <div>
                  <span className="text-sm text-white">{recording.name}</span>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{formatTime(recording.duration)}</span>
                    <span>•</span>
                    <span>{new Date(recording.timestamp).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <audio src={recording.url} controls className="h-8 w-32" />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => downloadRecording(recording)}
                  data-testid={`download-${recording.id}`}
                  className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors"
                >
                  <Download size={18} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => deleteRecording(recording.id)}
                  data-testid={`delete-${recording.id}`}
                  className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Square size={18} />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {isProcessing && (
        <div className="flex items-center justify-center gap-2 text-gray-400">
          <Loader2 className="animate-spin" size={18} />
          <span>Processing recording...</span>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
